import { UTCDate } from '@date-fns/utc';
import { AccountView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { ValueTone } from '../components/metrics/metric-card';

type CurrentCashDataTypes = {
  accounts: AccountView[];
  baseCurrency: SupportedCountryCurrency;
  startDate: Date;
  endDate: Date;
};

const getTrendFromNetFlow = (netCashFlowInTimeframe: number): 'increasing' | 'decreasing' | 'default' => {
  if (Math.abs(netCashFlowInTimeframe) < 0.01) return 'default';
  return netCashFlowInTimeframe > 0 ? 'increasing' : 'decreasing';
};

/**
 * Formats the percent change in balance. Uses a small threshold to:
 * - avoid division-by-zero when previousBalance is effectively 0
 * - prevent misleading huge percentages due to floating-point noise
 * For near-zero denominators, we map by sign to 100% / -100% / 0% for clear UX.
 */
const getPercentageChange = (netCashFlowInTimeframe: number, previousBalance: number): string => {
  if (Math.abs(previousBalance) < 0.01) {
    let percent = 0;
    if (netCashFlowInTimeframe > 0) {
      percent = 100;
    } else if (netCashFlowInTimeframe < 0) {
      percent = -100;
    }
    return `${percent.toFixed(1)}%`;
  }
  const percent = (netCashFlowInTimeframe / Math.abs(previousBalance)) * 100;
  return `${percent.toFixed(1)}%`;
};

export const getCurrentCashData = ({ accounts, baseCurrency, startDate, endDate }: CurrentCashDataTypes) => {
  if (!accounts || accounts.length === 0) {
    return {
      formattedCurrentCash: getAmountWithCurrency({
        amount: 0,
        currency: baseCurrency,
        isAccountBalance: true,
      }),
      trend: 'default' as const,
      percentageChange: '0%',
    };
  }

  const currentCashInBaseCurrency = accounts.reduce((acc, account) => acc + account.baseCurrencyAvailable, 0);
  const transactions = accounts.flatMap((account) =>
    (account.transactions || []).map(({ fspDate, baseCurrencyAmount, isCredit, vendor }) => ({
      fspDate,
      baseCurrencyAmount,
      isCredit,
      vendor,
    })),
  );

  const transactionsInTimeframe = transactions.filter((transaction) => {
    if (!transaction.fspDate) return false;
    const transactionDate = new UTCDate(transaction.fspDate);
    return transactionDate >= startDate && transactionDate <= endDate;
  });

  const netCashFlowInTimeframe = transactionsInTimeframe.reduce((acc, transaction) => {
    const amountAbs = Math.abs(transaction.baseCurrencyAmount || 0);
    const signedByCreditDebit = transaction.isCredit ? -amountAbs : amountAbs;
    return acc + signedByCreditDebit;
  }, 0);

  const netChangeInBalance = -netCashFlowInTimeframe;
  const previousBalance = currentCashInBaseCurrency - netChangeInBalance;

  const formattedCurrentCash = getAmountWithCurrency({
    amount: currentCashInBaseCurrency,
    currency: baseCurrency,
    isAccountBalance: true,
  });
  const trend = getTrendFromNetFlow(netChangeInBalance);
  const percentageChange = getPercentageChange(netChangeInBalance, previousBalance);

  let valueTone: ValueTone;
  if (trend === 'increasing') {
    valueTone = 'positive';
  } else if (trend === 'decreasing') {
    valueTone = 'negative';
  } else {
    valueTone = 'neutral';
  }

  return {
    currentCashInBaseCurrency,
    formattedCurrentCash,
    trend,
    percentageChange,
    valueTone,
  };
};
